/**
 * CORE-1133 (возврат QA 18.09) — правка названия, описания и обложки записи.
 *
 * Требование было в задаче с самого начала, но точки входа в архиве не
 * появилось: в меню строки жили публикация, шаблон и удаление. API при этом
 * умел всё нужное — и у комнат, и у эфиров, — поэтому «прогон API прошёл» и
 * «мастер может отредактировать» разошлись. Здесь собран недостающий слой.
 *
 * Запись живёт в одной из двух сущностей, и ручки у них разные:
 *
 *   комната  PATCH /api/watch-parties/{id}/archive   — частичное обновление,
 *            обложка отдаётся ссылкой, поднятой заранее;
 *   эфир     PUT   /api/master/content/lives/{id}    — форма, обложка файлом.
 *
 * Решение о маршруте и о том, что вообще отправлять, вынесено сюда: иначе оно
 * разъезжается с экраном при первой же правке.
 */
import type { LiveRecordingItem } from '../../../types/liveRecordings';

/**
 * CORE-1526 (п. 1) — то, что нужно редактору от записи.
 *
 * Редактор один на все входы: архив, страница записи эфира, страница итогов
 * комнаты. У каждой из них своя сущность на руках (`LiveRecordingItem`,
 * `LiveDetailResponse`, `RoomResponse`), и требовать от страницы полную строку
 * архива значило бы выдумывать `watchedCount` и `playbackUrl` ради формы,
 * которой они не нужны.
 */
export type RecordingEditable = Pick<
    LiveRecordingItem,
    'liveId' | 'source' | 'title' | 'description' | 'coverImageUrl'
>;

/** Что мастер ввёл в форме. */
export interface RecordingDraft {
    title: string;
    description: string;
    /** Ссылка на уже поднятую обложку — у комнаты. */
    coverImageUrl?: string | null;
    /** Файл обложки — у эфира он уходит прямо в форму. */
    coverFile?: File | null;
}

/** Что поменялось относительно записи. Пустой объект — менять нечего. */
export interface RecordingPatch {
    title?: string;
    description?: string;
    coverImageUrl?: string;
    coverFile?: File;
}

export const TITLE_MIN = 3;

/** Название обязательно: пустое имя записи в архиве не найти. */
export function isTitleValid(title: string): boolean {
    return title.trim().length >= TITLE_MIN;
}

const same = (a: string | null | undefined, b: string | null | undefined) =>
    (a ?? '').trim() === (b ?? '').trim();

/**
 * Разница между записью и черновиком.
 *
 * Отправляем только изменённое. У комнаты обновление частичное, и лишнее поле
 * перезаписало бы то, что мастер не трогал; у эфира форма собирается из того
 * же набора, и посылать неизменное описание незачем.
 */
export function recordingPatch(item: RecordingEditable, draft: RecordingDraft): RecordingPatch {
    const patch: RecordingPatch = {};

    if (!same(item.title, draft.title)) patch.title = draft.title.trim();
    if (!same(item.description, draft.description)) patch.description = draft.description.trim();

    if (draft.coverFile) patch.coverFile = draft.coverFile;
    else if (draft.coverImageUrl && !same(item.coverImageUrl, draft.coverImageUrl)) {
        patch.coverImageUrl = draft.coverImageUrl;
    }

    return patch;
}

export function hasChanges(patch: RecordingPatch): boolean {
    return Object.keys(patch).length > 0;
}

/**
 * Куда уходит правка.
 *
 * Источник у строки может отсутствовать — сборка клиента старше бэкенда его не
 * получит (см. `LiveRecordingItem.source`). Тогда считаем эфиром: так было до
 * появления комнат в архиве, и старое поведение остаётся прежним.
 */
export function editTarget(item: RecordingEditable): 'room' | 'live' {
    return item.source === 'room' ? 'room' : 'live';
}
